import React, { useState } from 'react';
import {
  Modal,
  BlockStack,
  TextField,
  Select,
  Button,
  InlineStack,
  Text,
  Box
} from '@shopify/polaris';
import { ticketsService } from '../../services/ticketsService';
import {
  TICKET_TYPES,
  TICKET_TYPE_LABELS,
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  PRIORITY_LEVELS,
  PRIORITY_LABELS,
  TICKET_SOURCES,
  TICKET_SOURCE_LABELS,
  DEFAULT_TICKET_FORM,
  VALIDATION_RULES
} from '../../constants/ticketConstants';

const CreateTicketModal = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  relatedOrderId = null
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    ...DEFAULT_TICKET_FORM,
    related_order_id: relatedOrderId || ''
  });

  const [errors, setErrors] = useState({});

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle customer field changes
  const handleCustomerFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        [field]: value
      }
    }));
    // Clear error for this field
    const errorKey = `customer.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate title
    if (!formData.title || formData.title.trim().length < VALIDATION_RULES.TITLE_MIN_LENGTH) {
      newErrors.title = `Title must be at least ${VALIDATION_RULES.TITLE_MIN_LENGTH} characters`;
    } else if (formData.title.length > VALIDATION_RULES.TITLE_MAX_LENGTH) {
      newErrors.title = `Title must not exceed ${VALIDATION_RULES.TITLE_MAX_LENGTH} characters`;
    }

    // Validate description
    if (!formData.description || formData.description.trim().length < VALIDATION_RULES.DESCRIPTION_MIN_LENGTH) {
      newErrors.description = `Description must be at least ${VALIDATION_RULES.DESCRIPTION_MIN_LENGTH} characters`;
    } else if (formData.description.length > VALIDATION_RULES.DESCRIPTION_MAX_LENGTH) {
      newErrors.description = `Description must not exceed ${VALIDATION_RULES.DESCRIPTION_MAX_LENGTH} characters`;
    }

    // Validate customer name
    if (!formData.customer.name || formData.customer.name.trim().length < 2) {
      newErrors['customer.name'] = 'Customer name is required';
    }

    // Validate phone if provided
    if (formData.customer.phone && !VALIDATION_RULES.PHONE_REGEX.test(formData.customer.phone)) {
      newErrors['customer.phone'] = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleCreateTicket = async () => {
    if (!validateForm()) {
      return;
    }

    setIsCreating(true);

    try {
      // Prepare ticket data
      const ticketData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        type: formData.type,
        category: formData.category,
        priority: formData.priority,
        customer: {
          name: formData.customer.name.trim(),
          email: formData.customer.email.trim(),
          phone: formData.customer.phone.trim() || undefined
        },
        related_order_id: formData.related_order_id || undefined,
        tags: formData.tags,
        source: formData.source
      };

      // Create ticket
      const response = await ticketsService.createTicket(ticketData);

      if (response.success) {
        // Reset form
        setFormData({
          ...DEFAULT_TICKET_FORM,
          related_order_id: relatedOrderId || ''
        });
        setErrors({});

        // Call success callback
        if (onSuccess) {
          onSuccess(response.data);
        }

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      if (onError) {
        onError(error.message || 'Failed to create ticket');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isCreating) {
      // Reset form
      setFormData({
        ...DEFAULT_TICKET_FORM,
        related_order_id: relatedOrderId || ''
      });
      setErrors({});
      onClose();
    }
  };

  // Prepare type options
  const typeOptions = Object.entries(TICKET_TYPES).map(([key, value]) => ({
    label: TICKET_TYPE_LABELS[value],
    value: value
  }));

  // Prepare category options
  const categoryOptions = Object.entries(TICKET_CATEGORIES).map(([key, value]) => ({
    label: TICKET_CATEGORY_LABELS[value],
    value: value
  }));

  // Prepare priority options
  const priorityOptions = Object.entries(PRIORITY_LEVELS).map(([key, value]) => ({
    label: PRIORITY_LABELS[value],
    value: value
  }));

  // Prepare source options
  const sourceOptions = Object.entries(TICKET_SOURCES).map(([key, value]) => ({
    label: TICKET_SOURCE_LABELS[value],
    value: value
  }));

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{padding: '16px'}}>{'Create New Ticket'}</div>}
      primaryAction={{
        content: 'Create Ticket',
        onAction: handleCreateTicket,
        loading: isCreating,
        disabled: isCreating
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
          disabled: isCreating
        }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Title */}
          <TextField
            label="Title"
            value={formData.title}
            onChange={(value) => handleFieldChange('title', value)}
            placeholder="Brief description of the issue"
            error={errors.title}
            autoComplete="off"
            maxLength={VALIDATION_RULES.TITLE_MAX_LENGTH}
            showCharacterCount
            requiredIndicator
          />

          {/* Description */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={(value) => handleFieldChange('description', value)}
            placeholder="Detailed description of the issue"
            multiline={4}
            error={errors.description}
            autoComplete="off"
            maxLength={VALIDATION_RULES.DESCRIPTION_MAX_LENGTH}
            showCharacterCount
            requiredIndicator
          />

          {/* Type and Category */}
          <InlineStack gap="400">
            <Box width="50%">
              <Select
                label="Type"
                options={typeOptions}
                value={formData.type}
                onChange={(value) => handleFieldChange('type', value)}
                requiredIndicator
              />
            </Box>
            <Box width="50%">
              <Select
                label="Category"
                options={categoryOptions}
                value={formData.category}
                onChange={(value) => handleFieldChange('category', value)}
                requiredIndicator
              />
            </Box>
          </InlineStack>

          {/* Priority */}
          <Select
            label="Priority"
            options={priorityOptions}
            value={formData.priority}
            onChange={(value) => handleFieldChange('priority', value)}
            helpText="Urgent tickets will be escalated automatically"
          />

          {/* Source */}
          <Select
            label="Source"
            options={sourceOptions}
            value={formData.source}
            onChange={(value) => handleFieldChange('source', value)}
            helpText="How this ticket was created or submitted"
          />

          {/* Customer Information */}
          <Box>
            <Text variant="headingMd" as="h3">Customer Information</Text>
          </Box>

          <TextField
            label="Customer Name"
            value={formData.customer.name}
            onChange={(value) => handleCustomerFieldChange('name', value)}
            placeholder="Full name"
            error={errors['customer.name']}
            autoComplete="name"
            requiredIndicator
          />

          <TextField
            label="Customer Email"
            value={formData.customer.email}
            onChange={(value) => handleCustomerFieldChange('email', value)}
            placeholder="email@example.com"
            type="email"
            error={errors['customer.email']}
            autoComplete="email"
          />

          <TextField
            label="Customer Phone"
            value={formData.customer.phone}
            onChange={(value) => handleCustomerFieldChange('phone', value)}
            placeholder="+1234567890"
            type="tel"
            error={errors['customer.phone']}
            autoComplete="tel"
          />

          {/* Related Order */}
          <TextField
            label="Related Order ID"
            value={formData.related_order_id}
            onChange={(value) => handleFieldChange('related_order_id', value)}
            placeholder="Optional - Link to an order"
            helpText="If this ticket is related to a specific order, enter the order ID"
            autoComplete="off"
          />

          {/* Tags */}
          <TextField
            label="Tags"
            value={formData.tags.join(', ')}
            onChange={(value) => handleFieldChange('tags', value ? value.split(',').map(t => t.trim()).filter(Boolean) : [])}
            placeholder="urgent, refund, quality (comma-separated)"
            helpText="Add tags to help organize and filter tickets"
            autoComplete="off"
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default CreateTicketModal;
